/**
 * The three cards about people: the passenger, their car & driver, and who
 * the team should contact.
 */
import { useTranslation } from "react-i18next";
import {
  CAR_DETAILS_LATER,
  CAR_DETAILS_READY,
  CONTACT_DIFFERENT,
  CONTACT_SAME,
  GROUP_AIRPORT,
  PICKUP_SIGN_CUSTOM,
  PICKUP_SIGN_SAME,
  TIER_VIP,
} from "../constants";
import { Fold } from "./animated";
import { ChoiceButton, ChoiceGroup, Field, PhoneNumberInput, TextInput } from "./fields";
import { FormCard } from "./layout";
import { EmailAndPhoneFields, NameWithTitleField } from "./PersonFields";

/**
 * White box that holds the extra fields revealed by a choice. It folds open
 * when the choice is made and folds away when the guest picks the other one.
 * The gap above lives inside the fold so it opens and closes with the box.
 */
const RevealedPanel = ({ isOpen, children }) => (
  <Fold isOpen={isOpen}>
    <div className="tw:pt-[6px]">
      <div className="tw:bg-white tw:border tw:border-solid tw:border-[#E8E8E8] tw:!rounded-[5px] tw:p-4 tw:lg:p-6 tw:flex tw:flex-col tw:gap-y-7">
        {children}
      </div>
    </div>
  </Fold>
);

/** Name board choice: the lead passenger's name, or custom text. */
const PickupSignField = ({ group, selectedTier, form, errors, updateField }) => {
  const { t } = useTranslation();
  const label = t(`meetAssistInquiry.PickupSign.Label.${group === GROUP_AIRPORT ? "airport" : "station"}`);

  return (
    <div className="tw:flex tw:flex-col tw:gap-y-[10px]">
      <ChoiceGroup label={label} isRequired>
        <ChoiceButton
          title={t("meetAssistInquiry.PickupSign.Same")}
          isSelected={form.pickupSignType === PICKUP_SIGN_SAME}
          onSelect={() => updateField("pickupSignType", PICKUP_SIGN_SAME)}
        />
        <ChoiceButton
          title={t("meetAssistInquiry.PickupSign.Custom")}
          isSelected={form.pickupSignType === PICKUP_SIGN_CUSTOM}
          onSelect={() => updateField("pickupSignType", PICKUP_SIGN_CUSTOM)}
        />
      </ChoiceGroup>

      <Fold isOpen={form.pickupSignType === PICKUP_SIGN_CUSTOM}>
        <div className="tw:bg-[#F9F9F9] tw:!rounded-[5px] tw:p-4 tw:flex tw:flex-col tw:gap-y-[10px] tw:font-hanken-grotesk">
          <label htmlFor="meet-assist-pickup-sign" className="tw:!mb-0 tw:text-[12px] tw:leading-[17px] tw:text-[#8D8D8D]">
            {label}
          </label>
          <TextInput
            id="meet-assist-pickup-sign"
            value={form.pickupSignText}
            errorKey={errors.pickupSignText}
            placeholder={t("meetAssistInquiry.PickupSign.Placeholder")}
            onChange={(text) => updateField("pickupSignText", text)}
          />
          {/* Only VIP has this restriction, so only VIP guests see it. */}
          <Fold isOpen={selectedTier === TIER_VIP}>
            <p className="tw:!mb-0 tw:text-[13px] tw:leading-[18.5px] tw:!text-[#666666]">
              {t("meetAssistInquiry.PickupSign.VipNote")}
            </p>
          </Fold>
        </div>
      </Fold>
    </div>
  );
};

export const PassengerInformationSection = ({ group, selectedTier, form, errors, updateField }) => {
  const { t } = useTranslation();
  return (
    <FormCard title={t("meetAssistInquiry.Sections.Passenger")}>
      <NameWithTitleField
        id="meet-assist-lead-name"
        label={t("meetAssistInquiry.Passenger.LeadName")}
        hint={t("meetAssistInquiry.Passenger.LeadNameHint")}
        title={form.leadTitle}
        name={form.leadName}
        errorKey={errors.leadName}
        onChangeTitle={(title) => updateField("leadTitle", title)}
        onChangeName={(name) => updateField("leadName", name)}
      />
      <PickupSignField group={group} selectedTier={selectedTier} form={form} errors={errors} updateField={updateField} />
      <EmailAndPhoneFields
        idPrefix="meet-assist-passenger"
        emailLabel={t("meetAssistInquiry.Passenger.Email")}
        phoneLabel={t("meetAssistInquiry.Passenger.Phone")}
        email={form.email}
        phone={form.phone}
        emailErrorKey={errors.email}
        phoneErrorKey={errors.phone}
        onChangeEmail={(email) => updateField("email", email)}
        onChangePhone={(phone) => updateField("phone", phone)}
      />
    </FormCard>
  );
};

export const CarDriverSection = ({ form, errors, updateField }) => {
  const { t } = useTranslation();
  return (
    <FormCard title={t("meetAssistInquiry.Sections.CarDriver")} intro={t("meetAssistInquiry.Sections.CarDriverIntro")}>
      <div>
        <ChoiceGroup>
          <ChoiceButton
            title={t("meetAssistInquiry.CarDriver.Ready")}
            description={t("meetAssistInquiry.CarDriver.ReadyDescription")}
            isSelected={form.carDetailsType === CAR_DETAILS_READY}
            onSelect={() => updateField("carDetailsType", CAR_DETAILS_READY)}
          />
          <ChoiceButton
            title={t("meetAssistInquiry.CarDriver.Later")}
            description={t("meetAssistInquiry.CarDriver.LaterDescription")}
            isSelected={form.carDetailsType === CAR_DETAILS_LATER}
            onSelect={() => updateField("carDetailsType", CAR_DETAILS_LATER)}
          />
        </ChoiceGroup>

        <RevealedPanel isOpen={form.carDetailsType === CAR_DETAILS_READY}>
            <Field id="meet-assist-car-plate" label={t("meetAssistInquiry.CarDriver.PlateNumber")} isRequired errorKey={errors.carPlateNumber}>
              <TextInput
                id="meet-assist-car-plate"
                value={form.carPlateNumber}
                errorKey={errors.carPlateNumber}
                placeholder={t("meetAssistInquiry.CarDriver.PlatePlaceholder")}
                onChange={(text) => updateField("carPlateNumber", text)}
              />
            </Field>
            <div className="tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-x-[10px] tw:gap-y-7">
              <Field id="meet-assist-driver-name" label={t("meetAssistInquiry.CarDriver.DriverName")} isRequired errorKey={errors.driverName}>
                <TextInput
                  id="meet-assist-driver-name"
                  value={form.driverName}
                  errorKey={errors.driverName}
                  placeholder={t("meetAssistInquiry.CarDriver.DriverName")}
                  onChange={(text) => updateField("driverName", text)}
                />
              </Field>
              <Field id="meet-assist-driver-phone" label={t("meetAssistInquiry.CarDriver.DriverPhone")} isRequired errorKey={errors.driverPhone}>
                <PhoneNumberInput
                  id="meet-assist-driver-phone"
                  value={form.driverPhone}
                  errorKey={errors.driverPhone}
                  placeholder={t("meetAssistInquiry.CarDriver.DriverPhone")}
                  onChange={(phone) => updateField("driverPhone", phone)}
                />
              </Field>
            </div>
        </RevealedPanel>
      </div>
    </FormCard>
  );
};

export const ContactSection = ({ form, errors, updateField }) => {
  const { t } = useTranslation();
  return (
    <FormCard title={t("meetAssistInquiry.Sections.Contact")}>
      <div>
        <ChoiceGroup>
          <ChoiceButton
            title={t("meetAssistInquiry.Contact.Same")}
            isSelected={form.contactType === CONTACT_SAME}
            onSelect={() => updateField("contactType", CONTACT_SAME)}
          />
          <ChoiceButton
            title={t("meetAssistInquiry.Contact.Different")}
            isSelected={form.contactType === CONTACT_DIFFERENT}
            onSelect={() => updateField("contactType", CONTACT_DIFFERENT)}
          />
        </ChoiceGroup>

        <RevealedPanel isOpen={form.contactType === CONTACT_DIFFERENT}>
            <NameWithTitleField
              id="meet-assist-contact-name"
              label={t("meetAssistInquiry.Contact.Name")}
              hint={t("meetAssistInquiry.Contact.NameHint")}
              title={form.contactTitle}
              name={form.contactName}
              errorKey={errors.contactName}
              onChangeTitle={(title) => updateField("contactTitle", title)}
              onChangeName={(name) => updateField("contactName", name)}
            />
            <EmailAndPhoneFields
              idPrefix="meet-assist-contact"
              emailLabel={t("meetAssistInquiry.Contact.Email")}
              phoneLabel={t("meetAssistInquiry.Contact.Phone")}
              email={form.contactEmail}
              phone={form.contactPhone}
              emailErrorKey={errors.contactEmail}
              phoneErrorKey={errors.contactPhone}
              onChangeEmail={(email) => updateField("contactEmail", email)}
              onChangePhone={(phone) => updateField("contactPhone", phone)}
            />
        </RevealedPanel>
      </div>
    </FormCard>
  );
};
